import utils from '@theme/utils'
import { ON_CHANGE_DEBOUNCE_TIMER, PUB_SUB_EVENTS } from './constants.js'
import showErrorPopup from './show-error-popup.js'

/* This functionality is written in a more functional style because we do not have to deal with many different modules interacting with each other. Sorry, future devs. */

// CONSTANTS
/** @type {string} The API path for the post code data */
const API_PATH = 'https://api.thetreeelves.com.au/post_codes/${postCode}'

// Utils - ordered alphabetically

// C
const component = (str) => document.querySelector(`[data-component-${str}]`)

// F
/**
 * Finds an item in an array by a given ID.
 *
 * @param {string} id - The ID to search for.
 * @returns {Function} - A function that takes an array and returns the matching item, or undefined.
 */
const findItemById = (id) => (items) => items.find((item) => item.id === id)

/**
 * Converts a price from cents to dollars with the symbol and decimals (no "AUD"
 * suffix, Jeremy 2026-10-20; the Liquid total uses `money` to match).
 *
 * @param {number} priceCents - The price in cents.
 * @returns {string} The price in dollars.
 */
const formatMoney = (priceCents) => {
  const priceDollars = priceCents / 100
  return `$${priceDollars.toFixed(2)}`
}

// G
/**
 * Gets all elements that match the pattern of `data-component-<component-name>`.
 *
 * @param {string} componentName - The name of the component to search for.
 * @returns {NodeListOf<Element>} - The NodeList of elements that match the pattern.
 */
const getComponents = (componentName) => document.querySelectorAll(`[data-component-${componentName}]`)

/**
 * Gets the value of the `data-has-linked-product` attribute.
 *
 * @param {Element} element - The element to get the data from.
 * @returns {string} - The value of the `data-has-linked-product` attribute.
 */
const getHasLinkedProductDataset = (element) => element.dataset.hasLinkedProduct

/**
 * Retrieves the line item key from the element's dataset.
 *
 * @param {Element} element - The element to extract the line item key from.
 * @returns {string} - The line item key.
 */
const getLineItemKeyFromElement = (element) => element.dataset.lineItemKey

/**
 * Gets the variant ID from the element's dataset.
 *
 * @param {Element} element - The element to extract the variant ID from.
 * @returns {string} - The variant ID.
 */
const getVariantIdFromElement = (element) => element.dataset.variantId

/**
 * Gets the attributes from the cart object
 * @param {Object} cart - The cart data object.
 * @returns {Object} - The attributes in the cart.
 * */
const getAttributes = (cart) => cart.attributes

/**
 * Gets the post code from the attributes object.
 * @param {Object} attributes - The attributes object.
 * @returns {string} - The post code.
 **/
const getPostCode = (attributes) => attributes['Post Code']

/**
 * Gets the items from the cart data.
 * @param {Object} cart - The cart data object.
 * @returns {Array<Object>} - The items in the cart.
 **/
const getItems = (cart) => cart.items

// I
/**
 * Checks if a string is 'true'.
 *
 * @param {string} string - The string to check.
 * @returns {boolean} - Whether the string is 'true'.
 */
const isStringTrue = (string) => string === 'true'

/**
 * Updates the cart attributes by setting specified keys to empty strings.
 *
 * @param {Object} attributes - The attributes to update (set to empty strings to remove).
 * @returns {Promise<Object>} - A promise that resolves to the updated cart data.
 */
const updateCartAttributes = async (attributes) => {
  const response = await fetch('/cart/update.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ attributes }),
  })

  const result = await response.json()
  return result
}

// U
const updateVariantToJustOne = async ({ lineItemKey }) => {
  const response = await fetch(`/cart/change.js`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ id: lineItemKey, quantity: 1 }),
  })

  const result = await response.json()
  return result
}

// P
const pipe =
  (...fns) =>
  (x) =>
    fns.reduce((v, f) => f(v), x)

/**
 * Fetches the current cart data.
 *
 * @returns {Promise<Object>} - A promise that resolves to the cart data.
 */
const getCart = async () => {
  const response = await fetch('/cart.js')
  const cart = await response.json()
  return cart
}

const components = getComponents('delivery-charge-product')

/**
 * Processes all components and removes cart attributes if necessary.
 */
const processAllComponents = async () => {
  const componentArray = Array.from(components)
  const buildPostCodeUrl = utils.pipe(getAttributes, getPostCode, (postCode) =>
    API_PATH.replace('${postCode}', postCode),
  )

  /**
   * Creates a reusable function to find an item in a cart by ID.
   *
   * @param {string} id - The ID to search for.
   * @returns {Function} - A function that takes a cart and returns the matching item.
   */
  const createFindDeliveryChargeItem = (id) => pipe(getItems, findItemById(id))

  const cartItems = componentArray.map((component) => {
    return {
      component,
      hasLinkedProduct: isStringTrue(getHasLinkedProductDataset(component)),
      lineItemKey: getLineItemKeyFromElement(component),
      variantId: getVariantIdFromElement(component),
    }
  })

  const hasUnlinkedProduct = cartItems.every((item) => !item.hasLinkedProduct)

  if (hasUnlinkedProduct) {
    const updates = Object.fromEntries(
      cartItems.filter((item) => !item.hasLinkedProduct).map((item) => [String(item.lineItemKey), 0]),
    )

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates }),
    })
      .then((r) => r.json())
      .then((data) => {
        const items = data.items
        const totalPriceCents = data.total_price
        const totalPriceDollars = formatMoney(totalPriceCents)
        const cartTotalElement = component('cart-total')
        if (cartTotalElement) {
          cartTotalElement.textContent = totalPriceDollars
        }
        const itemsCount = data.items.length

        console.log('Items count:', itemsCount)
        console.log('Items:', items)

        if (itemsCount === 1 && data.items[0].product_type === 'mws_fee_generated') {
          window.location.reload()
        }

        if (itemsCount === 0) {
          try {
            const cart = utils.component('cart')
            const cartItemsElement = utils.componentOf(cart, 'cart-items')
            const checkoutButton = utils.component('checkout-button')
            const cartFooter = utils.component('cart-footer')
            if (cart) {
              cart.classList.add('is-empty')
            }
            if (cartItemsElement) {
              cartItemsElement.remove()
            }
            if (cartFooter) {
              cartFooter.remove()
            }
          } catch (error) {
            showErrorPopup(error)
            console.error('Error removing cart items:', error)
          }
        }

        console.log('Success:', data)
      })
      .catch((err) => console.error('Error:', err))
  }

  const cart = await getCart()

  // Check if the cart is empty
  if (cart.items.length === 0) {
    // No items remaining in the cart, remove cart attributes
    const linkedCartAttributeKeys = [
      'Pickup Date',
      'Delivery Instructions',
      'Shipping method',
      'Shipping Date',
      'Location',
      'Shipping Date',
      'Additional Notes',
      'Post Code',
      'Store Address',
    ]

    const attributesToUpdate = {}

    linkedCartAttributeKeys.forEach((key) => {
      attributesToUpdate[key] = ''
    })

    await updateCartAttributes(attributesToUpdate)

    const newCart = await getCart()
    console.log('New cart', newCart)

    console.log('Removed linked cart attributes as the cart is empty')
  } else {
    console.log('Items remain in the cart, no need to remove cart attributes')
  }

  try {
    /** @type {Object} The cart data */
    const cart = await getCart()

    /** @type {string} The URL to fetch post code data */
    const postCodeApiUrl = buildPostCodeUrl(cart)

    /** @type {Response} The response from the fetch request */
    const response = await fetch(postCodeApiUrl)

    /** @type {Object} The post code data */
    const postCodeData = await response.json()

    /** @type {Delivery_Routes} The delivery routes object of the API response */
    const deliveryRoutes = postCodeData?.Delivery_Routes

    /** @type {string} the Delivery Charge Variant ID */
    const deliveryChargeVariantId = deliveryRoutes?.Delivery_charge_variantId

    /** @type {number} The Delivery Charge Variant ID as an integer */
    const deliverChargeVariantIdInt = parseInt(deliveryChargeVariantId)

    /** @type {Function} A function to find the delivery charge item in the cart */
    const findDeliveryChargeItem = createFindDeliveryChargeItem(deliverChargeVariantIdInt)

    /** @type {Object} The delivery charge item in the cart */
    const deliveryChargeItem = findDeliveryChargeItem(cart)

    if (deliveryChargeItem) {
      if (deliveryChargeItem.quantity > 1) {
        /** @type {string} The key of the delivery charge item */
        const key = deliveryChargeItem.key

        /** @type {Object} The response from updating the delivery charge item to just one */
        const changeResponse = await updateVariantToJustOne({ lineItemKey: key })
        console.log('changeResponse', changeResponse)
      }
    }
  } catch (error) {
    console.error('An error occurred while fetching the cart:', error)
  }
}

// Start the main process
processAllComponents().catch((error) => {
  console.error('An error occurred during processing:', error)
})
